import React, { useEffect, useRef, useState } from 'react';
import { Button, Select, Input, Modal, Tooltip, Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EditFilled, DeleteFilled } from '@ant-design/icons';
import Table from '../table';
import NotificationAlert from '../notification';
import {
  addNewTechSkill,
  addUpdateUserTechSkill,
  AddUpdateUserTechSkillReqType,
  getAllUserTechSkills,
  getTechnologiesByCategory,
  getTechnologyCategories,
  GetTechnologyCategoriesType,
  TechnologyCategory,
  UserTechSkillResponseType,
} from '../../services/skillMatrixService';
import { skillRatingCriteria } from '../../helper/constants';

const { Option } = Select;

interface SkillMatrixTableProps {
  userId: string;
  isDisableSave?: boolean;
  isDisabledMode?: boolean;
  onSave?: () => void;
}

// array 1-5
const ratingOptions = [1, 2, 3, 4, 5];

const SkillMatrixTable: React.FC<SkillMatrixTableProps> = ({
  userId,
  isDisableSave = false,
  isDisabledMode,
  onSave,
}) => {
  const myRef = useRef<any>();
  const [categories, setCategories] = useState<GetTechnologyCategoriesType[]>(
    []
  );
  const [userSkillMatrix, setUserSkillMatrix] = useState<
    UserTechSkillResponseType[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCatSelection, setIsLoadingCatSelection] =
    useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [technologyOptions, setTechnologyOptions] = useState<
    TechnologyCategory[]
  >([]);

  const [selectedSkill, setSelectedSkill] =
    useState<UserTechSkillResponseType | null>(null);

  const [lastSearchedTechnology, setLastSearchedTechnology] =
    useState<string>('');

  const [filteredTechnologyOptions, setFilteredTechnologyOptions] = useState<
    TechnologyCategory[]
  >([]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    myRef.current?.call(type, message);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await getTechnologyCategories();
        if (error) throw new Error(error.message);
        setCategories(data ?? []);
      } catch (error) {
        showNotification('error', (error as Error).message);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchUserTechSkills = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await getAllUserTechSkills(userId);
        if (error) throw new Error(error.message);
        setUserSkillMatrix(data ?? []);
      } catch (error) {
        showNotification('error', (error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserTechSkills();
  }, [userId]);

  const headers: ColumnsType<UserTechSkillResponseType> = [
    {
      title: 'Category of Technology',
      dataIndex: 'techSkill',
      render: (techSkill: { classification: { name: string } }) =>
        techSkill.classification.name,
      sorter: (a, b) =>
        a.techSkill.classification.name.localeCompare(
          b.techSkill.classification.name
        ),
    },
    {
      title: 'Technology',
      dataIndex: 'techSkill',
      render: (techSkill: { name: string }) => techSkill.name,
      sorter: (a, b) => a.techSkill.name.localeCompare(b.techSkill.name),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      render: (rating: number) => {
        const ratingInfo = skillRatingCriteria.find(
          (criteria) => criteria.level === rating
        );
        return (
          <div>
            {rating} - {ratingInfo ? ratingInfo.title : 'Unknown'}
            {/* <div className="text-sm text-gray-500">
              {ratingInfo ? ratingInfo.description.join(' ') : ''}
            </div> */}
          </div>
        );
      },
      sorter: (a, b) => a.rating - b.rating,
    },
    {
      title: 'Projects',
      dataIndex: 'numOfProjects',
      render: (numOfProjects: number) => numOfProjects,
      sorter: (a, b) => a.numOfProjects - b.numOfProjects,
    },
    {
      title: 'Years of Experience',
      dataIndex: 'experience',
      render: (experience: number) => experience,
      sorter: (a, b) => a.experience - b.experience,
    },
    {
      title: 'Action',
      key: 'Action',
      hidden: isDisabledMode,
      render: (skill) => (
        <div className="flex gap-x-[10px]">
          <Tooltip title="Edit Skill">
            <Button
              icon={<EditFilled />}
              onClick={() => {
                setSelectedSkill(skill);
                // Fetch technology options based on the selected category
                setIsLoadingCatSelection(true);
                getTechnologiesByCategory(skill.techSkill.classification.id)
                  .then(({ data, error }) => {
                    if (error) {
                      myRef.current?.call('error', error?.message);
                      return;
                    }
                    setTechnologyOptions(data ?? []); // Update technology options
                    setFilteredTechnologyOptions(data ?? []);
                    form.setFieldsValue({ technology: skill.techSkill.id }); // Set the technology field after fetching

                    form.setFieldsValue({
                      category: skill.techSkill.classification.id,
                      technology: skill.techSkill.id,
                      rating: skill.rating,
                      projects: skill.numOfProjects,
                      experience: skill.experience,
                    });
                    showModal();
                  })
                  .finally(() => setIsLoadingCatSelection(false));
              }}
            />
          </Tooltip>

          <Tooltip title="Delete Skill">
            <Button
              icon={<DeleteFilled />}
              onClick={() => handleDeleteSkill(skill)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Check if the skill already exists
      const skillExists = userSkillMatrix.some(
        (skill) =>
          skill.techSkill.id === values.technology &&
          skill.rating === values.rating &&
          skill.numOfProjects === values.projects &&
          skill.experience === values.experience
      );

      // If the skill exists, show a notification and do not add it
      if (skillExists) {
        showNotification('error', 'This skill already exists!');
        return; // Exit the function early
      }

      const reqData: AddUpdateUserTechSkillReqType[] = [
        ...userSkillMatrix
          .map((skill) =>
            skill.techSkill.id === selectedSkill?.techSkill.id
              ? {
                  UserId: userId,
                  SkillId: values.technology,
                  Rating: values.rating,
                  NumOfProjects: values.projects,
                  Experience: values.experience,
                }
              : skill
          )
          .filter(
            (skill): skill is AddUpdateUserTechSkillReqType => skill !== null
          ),
        // Proceed to add a new skill if it doesn't exist
        ...(selectedSkill === null
          ? [
              {
                UserId: userId,
                SkillId: values.technology,
                Rating: values.rating,
                NumOfProjects: values.projects,
                Experience: values.experience,
              },
            ]
          : []),
      ];

      const { data, error } = await addUpdateUserTechSkill(reqData);
      if (error) throw new Error(error.message);
      setUserSkillMatrix([...data]);
      showNotification(
        'success',
        selectedSkill
          ? 'Skill updated successfully!'
          : 'Skill added successfully!'
      );
    } catch (error) {
      showNotification('error', (error as Error).message);
    } finally {
      setIsModalVisible(false);
      setSelectedSkill(null);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleDeleteSkill = async (skill: UserTechSkillResponseType) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this skill?',
      onOk: async () => {
        const updatedSkillMatrix = userSkillMatrix.filter(
          (item) => item.techSkill.id !== skill.techSkill.id
        );
        const reqData: AddUpdateUserTechSkillReqType[] = updatedSkillMatrix.map(
          (item) => ({
            UserId: userId,
            SkillId: item.techSkill.id,
            Rating: item.rating,
            NumOfProjects: item.numOfProjects,
            Experience: item.experience,
          })
        );

        try {
          const { error } = await addUpdateUserTechSkill(reqData);
          if (error) throw new Error(error.message);
          setUserSkillMatrix(updatedSkillMatrix);
          showNotification('success', 'Skill deleted successfully!');
        } catch (error) {
          showNotification('error', (error as Error).message);
        }
      },
    });
  };

  // Function to check form validity
  const checkFormValidity = async () => {
    try {
      const values = form.getFieldsValue();
      const requiredFields = [
        'category',
        'technology',
        'rating',
        'projects',
        'experience',
      ];
      const allFieldsFilled = requiredFields.every((field) => {
        const value = values[field];
        return value !== undefined && value !== null && value !== '';
      });

      // rating must be between 1 and 5
      const rating = Number(values.rating);
      if (rating < 1 || rating > 5) {
        setIsFormValid(false);
        return;
      }

      // projects must be a positive number
      const projects = Number(values.projects);
      if (projects < 0) {
        setIsFormValid(false);
        return;
      }

      // experience must be a positive number
      const experience = Number(values.experience);
      if (experience < 0) {
        setIsFormValid(false);
        return;
      }

      // Check if the technology already exists in the list of technologies if editing
      const technologyExists = !selectedSkill
        ? userSkillMatrix.some(
            (skill) => skill.techSkill.id === values.technology
          )
        : false;

      if (selectedSkill && allFieldsFilled) {
        // Check if current form values match the selected skill's values
        const matchesOriginal =
          values.category === selectedSkill.techSkill.classification.id &&
          values.technology === selectedSkill.techSkill.id &&
          values.rating === selectedSkill.rating &&
          values.projects === selectedSkill.numOfProjects &&
          values.experience === selectedSkill.experience.toString();

        // Disable the button if values match the original
        setIsFormValid(!matchesOriginal && !technologyExists);
      } else {
        setIsFormValid(allFieldsFilled && !technologyExists);
      }
    } catch (error) {
      setIsFormValid(false);
    }
  };

  const handleTechnologyChange = (value: string) => {
    const technologyExists = userSkillMatrix.some(
      (skill) =>
        skill.techSkill.id === value &&
        skill.techSkill.classificationId === form.getFieldValue('category')
    );

    if (technologyExists) {
      // Set error message if technology already exists
      form.setFields([
        {
          name: 'technology',
          errors: ['This technology already exists in your skill matrix!'],
        },
      ]);
    } else {
      // Clear error message if technology does not exist
      form.setFields([
        {
          name: 'technology',
          errors: [],
        },
      ]);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Technical Skill Matrix</h2>
        {!isDisabledMode && (
          <div className="flex">
            {onSave && (
              <Button
                disabled={isDisableSave}
                type="default"
                className="mr-2"
                onClick={onSave}
              >
                Save Changes
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => {
                setSelectedSkill(null);
                form.resetFields();
                showModal();
              }}
            >
              Add New Skill
            </Button>
          </div>
        )}
      </div>

      {/** Criteria */}
      <table className="min-w-full border-collapse mb-2">
        <thead>
          <tr className="bg-gray-50">
            <th className="border px-4 py-2 text-left font-semibold">
              Technical Skill Rating
            </th>
            <th className="border px-4 py-2 text-left font-semibold">
              Technical Skill Rating Description
            </th>
          </tr>
        </thead>
        <tbody>
          {skillRatingCriteria.map((skill) => (
            <tr key={skill.level} className="hover:bg-gray-100">
              <td className="border px-4 py-2">
                <strong>{`${skill.title} - ${skill.level}`}</strong>
              </td>
              <td className="border px-4 py-2">
                {skill.description.join(' ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Table
        columns={headers}
        data={userSkillMatrix}
        errorFetchingData={false}
        loading={isLoading}
        rowKey="id"
      />

      <Modal
        title={selectedSkill ? 'Edit Skill' : 'Add New Skill'}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleOk}
            disabled={!isFormValid}
          >
            {selectedSkill ? 'Confirm' : 'Create'}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onValuesChange={checkFormValidity}>
          <Form.Item
            label="Category of Technology"
            name="category"
            rules={[{ required: true, message: 'Please select a category!' }]}
          >
            <Select
              placeholder="Select a category"
              showSearch
              onChange={(value) => {
                setIsLoadingCatSelection(true);
                // Fetch technology options based on selected category
                getTechnologiesByCategory(value)
                  .then(({ data, error }) => {
                    if (error) {
                      myRef.current?.call('error', error?.message);
                      return;
                    }
                    setTechnologyOptions(data ?? []); // Update technology options
                    setFilteredTechnologyOptions(data ?? []); // Update technology options
                    form.resetFields([
                      'technology',
                      'rating',
                      'projects',
                      'experience',
                    ]); // Reset other fields
                  })
                  .finally(() => setIsLoadingCatSelection(false));
              }}
              filterOption={(input, option) => {
                return (
                  //@ts-ignore
                  option?.children
                    //@ts-ignore
                    .toLowerCase()
                    .includes(input.toLowerCase())
                );
              }}
            >
              {categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {form.getFieldValue('category') && ( // Check if category is selected
            <Form.Item
              label="Technology"
              name="technology"
              rules={[
                { required: true, message: 'Please select a technology!' },
                {
                  validator: (_, value) => {
                    const technologyExists = userSkillMatrix.some(
                      (skill) =>
                        skill.techSkill.id === value &&
                        skill.techSkill.classificationId ===
                          form.getFieldValue('category') &&
                        // Allow the current technology when editing
                        (!selectedSkill ||
                          skill.techSkill.id !== selectedSkill.techSkill.id)
                    );
                    if (technologyExists) {
                      return Promise.reject(
                        new Error(
                          'This technology already exists in your skill matrix!'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                placeholder="Select a technology"
                loading={isLoadingCatSelection}
                showSearch
                onSearch={(value) => {
                  setLastSearchedTechnology(value);
                  // Filter technology options based on the search value
                  const filteredOptions = technologyOptions.filter((option) =>
                    option.name.toLowerCase().includes(value.toLowerCase())
                  );
                  setFilteredTechnologyOptions(filteredOptions); // Update filtered options
                }}
                onChange={handleTechnologyChange}
                filterOption={(input, option) => {
                  //@ts-ignore
                  return (option.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                notFoundContent={
                  <div>
                    No existing technical skill found. Please add this as a new
                    skill.
                  </div>
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Button
                      type="link"
                      onClick={() => {
                        if (lastSearchedTechnology) {
                          // Logic to add the new technology
                          addNewTechSkill({
                            ClassificationId: form.getFieldValue('category'),
                            Name: lastSearchedTechnology,
                          }).then(({ data }) => {
                            setTechnologyOptions(data);
                          });
                        }
                      }}
                      disabled={
                        !Boolean(lastSearchedTechnology) ||
                        filteredTechnologyOptions.length > 0
                      }
                    >
                      Create New Technology
                    </Button>
                  </>
                )}
              >
                {filteredTechnologyOptions.length > 0 // Use filtered options for display
                  ? filteredTechnologyOptions.map((option) => (
                      <Option key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))
                  : technologyOptions.map(
                      (
                        option // Fallback to original options if no filtered options
                      ) => (
                        <Option key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      )
                    )}
              </Select>
            </Form.Item>
          )}

          {form.getFieldValue('category') && (
            <Form.Item
              label="Rating"
              name="rating"
              rules={[{ required: true, message: 'Please select a rating!' }]}
            >
              <Select placeholder="Select a rating">
                {ratingOptions.map((rating) => {
                  const ratingInfo = skillRatingCriteria.find(
                    (criteria) => criteria.level === rating
                  );
                  return (
                    <Option key={rating} value={rating}>
                      {rating} - {ratingInfo ? ratingInfo.title : 'Unknown'}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {form.getFieldValue('category') && (
            <Form.Item
              label="Projects"
              name="projects"
              rules={[
                {
                  required: true,
                  message: 'Please enter the number of projects!',
                },
              ]}
            >
              <Input type="number" placeholder="Enter number of projects" />
            </Form.Item>
          )}

          {form.getFieldValue('category') && (
            <Form.Item
              label="Years of Experience"
              name="experience"
              rules={[
                {
                  required: true,
                  message: 'Please enter years of experience!',
                },
              ]}
            >
              <Input type="number" placeholder="Enter years of experience" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default SkillMatrixTable;
